import { FullScreenProgress, Typography } from '@occc/ui-design-system';

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client/core/networkStatus';

import { useGetLocationsQuery } from '@occc-app/dataAccessLayer/location/queries';
import { useGetMembershipById } from '@occc-app/dataAccessLayer/membership/queries';
import { useChainConfiguration } from '@occc-app/Chain/hooks';
import { useEaivStatistics } from '@occc-app/dataAccessLayer/eaiv/queries';
import { useGetPhoneNumberMetadataQuery } from '@occc/graphql';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useToggle } from '@occc/utils';

import { useConfig } from '@occc/config';
import { MemberDetailsPage } from './MemberDetailsPage';
import { errorMessage, noDataMessage } from './copies';
import { useMembershipEmployeeData } from './hooks/useMembershipEmployeeData';

export const MemberDetailsPageContainer = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { manualPointChangeCap } = useChainConfiguration();
  const {
    data: member,
    error: memberError,
    loading: memberLoading,
    networkStatus,
    refetch: refetchMembership,
  } = useGetMembershipById(id);

  const {
    data: phoneNumberMetadata,
    loading: isPhoneNumberMetadataLoading,
    error: isPhoneNumberMetadataError,
  } = useGetPhoneNumberMetadataQuery({
    variables: { phoneNumber: member?.phone || '' },
    skip: !member?.phone,
  });

  const {
    data: locations,
    error: locationsError,
    loading: locationsLoading,
  } = useGetLocationsQuery({
    skip: !member?.homeStores?.length,
    variables: {
      filter: {
        uuidContains: member?.homeStores?.map((store) => store.locationUuid),
      },
    },
  });

  const { statistics: eaivStatistics } = useEaivStatistics({
    customerUuid: member?.customerUuid || '',
  });

  const employeeData = useMembershipEmployeeData(member);

  const [showEditModal, toggleEditModal] = useToggle();
  const [showUpdatePointsModal, toggleUpdatePointsModal] = useToggle();
  const [showCreateCommentModal, toggleCreateCommentModal] = useToggle();

  const loading = useMemo(() => {
    return (memberLoading && networkStatus !== NetworkStatus.refetch) || employeeData.loading;
  }, [memberLoading, networkStatus, employeeData.loading]);

  const {
    config: {
      csrLogin: { enabled: isCSRLoginEnabled },
    },
  } = useConfig();
  const [isCSRLoginStarted, toggleIsCSRLoginStarted] = useToggle();

  if (loading) return <FullScreenProgress />;

  if (memberError) {
    return (
      <Typography color='error' data-testid='something-went-wrong-msg'>
        {errorMessage}
      </Typography>
    );
  }

  if (!member) {
    return (
      <Typography color='error' data-testid='no-data-msg'>
        {noDataMessage}
      </Typography>
    );
  }
  return (
    <MemberDetailsPage
      isCSRLoginEnabled={isCSRLoginEnabled}
      isCSRLoginStarted={isCSRLoginStarted}
      toggleIsCSRLoginStarted={toggleIsCSRLoginStarted}
      onCSRLoginCancelled={toggleIsCSRLoginStarted}
      toggleEditModal={toggleEditModal}
      toggleUpdatePointsModal={toggleUpdatePointsModal}
      toggleCreateCommentModal={toggleCreateCommentModal}
      member={member}
      showEditModal={showEditModal}
      showUpdatePointsModal={showUpdatePointsModal}
      showCreateCommentModal={showCreateCommentModal}
      locations={locations}
      locationsLoading={locationsLoading}
      locationsError={locationsError}
      eaivStatistics={eaivStatistics}
      isPointsAdjustmentEnabled={manualPointChangeCap !== 0 && Boolean(member.customerUuid)}
      employeeData={employeeData}
      refetchMembership={refetchMembership}
      phoneNumberMetadata={phoneNumberMetadata?.getPhoneNumberMetadata}
      isPhoneNumberMetadataLoading={isPhoneNumberMetadataLoading}
      isPhoneNumberMetadataError={isPhoneNumberMetadataError}
    />
  );
};
